import { EditorState } from 'lexical'
import { AiSparkleIcon } from '@/components/icons/AiSparkleIcon'
import React, {
    ReactElement,
    useCallback,
    useMemo,
    useState,
    useEffect,
} from 'react'
import styles from './NotecardEditor.module.less'
import {
    NotecardEditorContentType,
    NotecardEditorProps,
} from '@/components/ContentTreeNode/ContentTreeLeafNode/NotecardsEditor/NotecardEditor/NotecardEditor.types'
import {
    Alert,
    Button,
    Input,
    message,
    Popconfirm,
    Tabs,
    Tooltip,
    Typography,
} from 'antd'
import { MagicTextBoxEditor } from '@/components/ContentTreeNode/ContentTreeLeafNode/MagicTextBoxEditor/MagicTextBoxEditor'
import { NotecardMetadataEditor } from '@/components/ContentTreeNode/ContentTreeLeafNode/NotecardsEditor/NotecardEditor/NotecardMetadataEditor/NotecardMetadataEditor'
import {
    ContentCreatorEditableNotecardMetadata,
    parseContentCreatorEditableNotecardMetadata,
} from '@/components/ContentTreeNode/ContentTreeLeafNode/NotecardsEditor/NotecardEditor/NotecardMetadataEditor/NotecardMetadataEditor.types'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
    notecardAtomFamily,
    lastSavedNotecardDataAtomFamily,
} from '@/atoms/notecard'
import {
    setContentCreatorEditableNotecardMetadata,
    setNotecardFront,
    setNotecardBack,
} from '@/components/ContentTreeNode/ContentTreeLeafNode/NotecardsEditor/immer'
import {
    authStateAtom,
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import { overwriteNotecardAtContentPath } from '@/api/cb/contentCreatorContent'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'
import { CopyIndividualContentButton } from '@/components/ContentTreeNode/ContentTreeLeafNode/CopyIndividualContentButton/CopyIndividualContentButton'
import { MoveIndividualContentButton } from '@/components/ContentTreeNode/ContentTreeLeafNode/MoveIndividualContentButton/MoveIndividualContentButton'
import { activeNotecardEditorContentTypeAtom } from '@/atoms/activeNotecardEditorContentType'
import { ContentType } from 'common/src/commentThread/types'
import { CopyDeepLinkButton } from '@/components/CopyDeepLinkButton/CopyDeepLinkButton'
import { ContentType as UniversalContentType } from 'common/src/utils/buildUniversalContentLink'
import { useRouter } from 'next/router'
import { ContentCommentThreadWrapper } from '@/components/ContentCommentThread/ContentCommentThreadWrapper'
import { newlyCreatedAndUnsavedNotecardIDsAtom } from '@/atoms/newlyCreatedAndUnsavedContentIDs'
import { useMonitorNotecardChanges } from '@/components/ContentTreeNode/ContentTreeLeafNode/NotecardsEditor/NotecardEditor/useMonitorNotecardChanges'
import { parseSavableNotecardData } from '@/components/ContentTreeNode/ContentTreeLeafNode/NotecardsEditor/NotecardEditor/saving'
import {
    specificContentHasUnsavedChangesSelector,
    SavableContent,
    SavableContentType,
} from '@/atoms/unsavedChanges'
import { serializeEditorStateToString } from 'magic-text-box-lexical/src/serialization/serializeEditorStateToString'
import { useDebounce } from 'magic-text-box-lexical/src/utils/useDebounce'
import { PracticeProblemEditor } from '@/components/ContentTreeNode/ContentTreeLeafNode/PracticeProblemsEditor/PracticeProblemEditor/PracticeProblemEditor'
import {
    lastSavedPracticeProblemDataAtomFamily,
    practiceProblemAtomFamily,
    useInvalidatePracticeProblem,
} from '@/atoms/practiceProblem'
import { slimLeafNodeInvalidationAtomFamily } from '@/atoms/slimLeafNode'
import { hashContentPath } from 'common/src/ContentPath'
import {
    getAgentContentJob,
    getNotecardAgentJobs,
    startAgentContentJob,
} from '@/api/cb/agentContent'
import {
    AgentContentJobMode,
    AgentContentJobStatus,
    AgentJobHistoryEntry,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { AnthropicModelName } from 'common/src/api/contentFrontendVsContentBackend/llm'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { agentActivityAtom } from '@/atoms/agentActivity'
import {
    AGENT_MODE_LABEL,
    getAgentModelLabel,
} from '@/components/GlobalAgentActivity/agentActivityLabels'

const COUNTERPART_MODEL = AnthropicModelName['claude-sonnet-5-5']

const formatRelativeTime = (isoTimestamp: string): string => {
    const deltaMs = Date.now() - new Date(isoTimestamp).getTime()
    const relative = new Intl.RelativeTimeFormat(undefined, {
        numeric: 'auto',
    })
    const minutes = Math.round(deltaMs / 60_000)
    if (minutes < 60) return relative.format(-minutes, 'minute')
    const hours = Math.round(minutes / 60)
    if (hours < 24) return relative.format(-hours, 'hour')
    return relative.format(-Math.round(hours / 24), 'day')
}

interface JustStartedConversion {
    jobId: string | null
    createdAt: string
}

export const NotecardEditor: React.FC<NotecardEditorProps> = (
    props
): ReactElement => {
    const [notecard, setNotecard] = useRecoilState(
        notecardAtomFamily(props.notecardID)
    )
    useMonitorNotecardChanges(notecard.id, notecard.contentPath)
    const authState = useRecoilValue(authStateAtom)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)

    const contentCreatorEditableNotecardMetadata = useMemo(
        (): ContentCreatorEditableNotecardMetadata =>
            parseContentCreatorEditableNotecardMetadata(notecard),
        [notecard]
    )

    const setContentCreatorEditableNotecardMetadataCallback = useCallback(
        (
            contentCreatorEditableNotecardMetadata: ContentCreatorEditableNotecardMetadata
        ) => {
            setNotecard((notecard) =>
                setContentCreatorEditableNotecardMetadata(
                    notecard,
                    contentCreatorEditableNotecardMetadata
                )
            )
        },
        [setNotecard]
    )

    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [
        newlyCreatedAndUnsavedNotecardIDs,
        setNewlyCreatedAndUnsavedNotecardIDs,
    ] = useRecoilState(newlyCreatedAndUnsavedNotecardIDsAtom)
    const hasNeverBeenSaved = useMemo(
        (): boolean => newlyCreatedAndUnsavedNotecardIDs.has(notecard.id),
        [notecard, newlyCreatedAndUnsavedNotecardIDs]
    )
    const setLastSavedNotecardData = useSetRecoilState(
        lastSavedNotecardDataAtomFamily(notecard.id)
    )
    const saveNotecardChanges = useCallback(async (): Promise<void> => {
        if (isReadOnly) return
        setIsSaving(true)
        const response = await overwriteNotecardAtContentPath(
            courseName,
            notecard.contentPath,
            notecard
        )
        if (response.data.isError) {
            syncErrorMessage(
                'Error saving notecard. You may want to try saving the notecard again.'
            )
        } else {
            setLastSavedNotecardData(parseSavableNotecardData(notecard))
            syncSuccessMessage('Notecard successfully saved')
            setNewlyCreatedAndUnsavedNotecardIDs((current) =>
                current.remove(notecard.id)
            )
        }
        setIsSaving(false)
    }, [
        courseName,
        isReadOnly,
        notecard,
        setLastSavedNotecardData,
        setNewlyCreatedAndUnsavedNotecardIDs,
    ])

    const [
        activeNotecardEditorContentType,
        setActiveNotecardEditorContentType,
    ] = useRecoilState(activeNotecardEditorContentTypeAtom)

    // quick trick to quickly route to the comment thread
    const router = useRouter()
    useEffect(() => {
        if (router.query.showCommentThread) {
            setActiveNotecardEditorContentType(
                NotecardEditorContentType.commentThread
            )
            const newRouterQuery = { ...router.query }
            delete newRouterQuery.showCommentThread
            router.replace({
                pathname: router.pathname,
                query: newRouterQuery,
            })
        }
    }, [router, setActiveNotecardEditorContentType])

    const savableContent = useMemo(
        (): SavableContent => ({
            contentPath: notecard.contentPath,
            contentType: SavableContentType.notecard,
            id: notecard.id,
        }),
        [notecard.id, notecard.contentPath]
    )
    const isUnsaved = useRecoilValue(
        specificContentHasUnsavedChangesSelector(savableContent)
    )

    const debouncedNotecardFrontUpdateListener = useDebounce(
        (editorState: EditorState): void => {
            const newNotecardFront = serializeEditorStateToString(editorState)
            setNotecard((notecard) =>
                setNotecardFront(notecard, newNotecardFront)
            )
        },
        250,
        1_000
    )

    const debouncedNotecardBackUpdateListener = useDebounce(
        (editorState: EditorState): void => {
            const newNotecardFront = serializeEditorStateToString(editorState)
            setNotecard((notecard) =>
                setNotecardBack(notecard, newNotecardFront)
            )
        },
        250,
        1_000
    )

    const notecardAsPracticeProblem = useRecoilValue(
        practiceProblemAtomFamily(props.notecardID)
    )
    const invalidatePracticeProblem = useInvalidatePracticeProblem(
        props.notecardID
    )
    const setLastSavedCounterpartData = useSetRecoilState(
        lastSavedPracticeProblemDataAtomFamily(props.notecardID)
    )
    const activity = useRecoilValue(agentActivityAtom)
    const setViewingState = useSetRecoilState(viewingStateAtom)
    const [
        isConvertingNotecardToPracticeProblem,
        setIsConvertingNotecardToPracticeProblem,
    ] = useState<boolean>(false)
    const [justStartedConversion, setJustStartedConversion] =
        useState<JustStartedConversion | null>(null)
    const [trackedConversionJobId, setTrackedConversionJobId] = useState<
        string | null
    >(null)
    const [conversionErrorJobId, setConversionErrorJobId] = useState<
        string | null
    >(null)
    const [steeringText, setSteeringText] = useState<string>('')
    const [cardJobHistory, setCardJobHistory] = useState<
        AgentJobHistoryEntry[] | null
    >(null)
    const refreshCardJobHistory = useCallback(async (): Promise<void> => {
        const response = await getNotecardAgentJobs({
            courseName,
            notecardId: props.notecardID,
        })
        if (response.data.success) {
            setCardJobHistory(response.data.payload.entries)
        }
    }, [courseName, props.notecardID])
    useEffect(() => {
        void refreshCardJobHistory()
    }, [refreshCardJobHistory])
    const activeConversionJob = useMemo(
        () =>
            activity?.activeJobs.find(
                (job) =>
                    job.mode === AgentContentJobMode.counterpartBackfill &&
                    job.courseName === courseName &&
                    job.targetNotecardIds?.includes(props.notecardID) &&
                    (job.status === AgentContentJobStatus.queued ||
                        job.status === AgentContentJobStatus.waitingForLock ||
                        job.status === AgentContentJobStatus.running)
            ) ?? null,
        [activity?.activeJobs, courseName, props.notecardID]
    )
    const openRunDetails = useCallback(
        (jobId: string): void => {
            setViewingState((viewingState) => ({
                ...viewingState,
                viewingStateType: ViewingStateType.agentActivity,
                id: jobId,
            }))
        },
        [setViewingState]
    )

    useEffect(() => {
        if (!activeConversionJob) return
        setTrackedConversionJobId(activeConversionJob.jobId)
        setJustStartedConversion(null)
        setConversionErrorJobId(null)
    }, [activeConversionJob])

    useEffect(() => {
        if (!activity || activeConversionJob) return
        const jobId =
            trackedConversionJobId ?? justStartedConversion?.jobId ?? null
        if (!jobId) return
        let isMounted = true
        void (async (): Promise<void> => {
            const response = await getAgentContentJob({ jobId })
            if (!isMounted || !response.data.success) return
            const completedJob = response.data.payload.job
            if (
                completedJob.status === AgentContentJobStatus.queued ||
                completedJob.status === AgentContentJobStatus.waitingForLock ||
                completedJob.status === AgentContentJobStatus.running
            ) {
                return
            }
            setTrackedConversionJobId(null)
            setJustStartedConversion(null)
            if (completedJob.status === AgentContentJobStatus.succeeded) {
                setConversionErrorJobId(null)
                // Reset the saved-baseline BEFORE invalidating so the monitor
                // re-primes from the agent's freshly applied content instead of
                // flagging it as phantom unsaved local edits
                setLastSavedCounterpartData(null)
                invalidatePracticeProblem()
                syncSuccessMessage('Practice problem generated.')
            } else {
                setConversionErrorJobId(jobId)
            }
            void refreshCardJobHistory()
        })()
        return () => {
            isMounted = false
        }
    }, [
        activeConversionJob,
        activity,
        invalidatePracticeProblem,
        justStartedConversion?.jobId,
        refreshCardJobHistory,
        setLastSavedCounterpartData,
        trackedConversionJobId,
    ])

    const convertNotecardToPracticeProblemCallback = useCallback(
        async (modifyExisting: boolean): Promise<void> => {
            setIsConvertingNotecardToPracticeProblem(true)
            setConversionErrorJobId(null)
            setJustStartedConversion({
                jobId: null,
                createdAt: new Date().toISOString(),
            })
            try {
                const response = await startAgentContentJob({
                    courseName,
                    contentPath: notecard.contentPath,
                    mode: AgentContentJobMode.counterpartBackfill,
                    modelName: COUNTERPART_MODEL,
                    targetNotecardIds: [props.notecardID],
                    instructorRequest: steeringText.trim() || undefined,
                    counterpartModifyExisting: modifyExisting || undefined,
                })
                if (!response.data.success) {
                    syncErrorMessage(
                        `Failed to start conversion agent: ${response.data.error}`
                    )
                    setJustStartedConversion(null)
                    return
                }
                const jobId = response.data.payload.jobId
                setTrackedConversionJobId(jobId)
                setJustStartedConversion((current) => ({
                    jobId,
                    createdAt: current?.createdAt ?? new Date().toISOString(),
                }))
                message.success({
                    content: (
                        <span>
                            {response.data.payload.waitingOnJobId
                                ? 'Run queued behind another job — it starts automatically when that run finishes. '
                                : 'Conversion agent started. Results will appear in Agent Activity. '}
                            <Button
                                type={'link'}
                                size={'small'}
                                onClick={() => openRunDetails(jobId)}
                            >
                                View run
                            </Button>
                        </span>
                    ),
                    duration: 8,
                })
            } finally {
                setIsConvertingNotecardToPracticeProblem(false)
            }
        },
        [
            courseName,
            notecard.contentPath,
            openRunDetails,
            props.notecardID,
            steeringText,
        ]
    )
    const doesNotecardAsPracticeProblemExist = useMemo(
        (): boolean => notecardAsPracticeProblem !== null,
        [notecardAsPracticeProblem]
    )
    const convertNotecardToPracticeProblemButtonDisabledText =
        useMemo((): string => {
            if (isUnsaved || hasNeverBeenSaved) {
                return 'Please save notecard before converting to practice problem'
            } else {
                return ''
            }
        }, [hasNeverBeenSaved, isUnsaved])

    const hashedContentPath = useMemo(
        (): string => hashContentPath(notecard.contentPath),
        [notecard.contentPath]
    )
    const slimLeafNodeInvalidator = useRecoilValue(
        slimLeafNodeInvalidationAtomFamily(hashedContentPath)
    )
    const displayedConversion = activeConversionJob
        ? {
              createdAt:
                  activeConversionJob.startedAt ??
                  activeConversionJob.createdAt,
              jobId: activeConversionJob.jobId,
              status: activeConversionJob.status,
          }
        : justStartedConversion
          ? {
                createdAt: justStartedConversion.createdAt,
                jobId: justStartedConversion.jobId,
                status: AgentContentJobStatus.queued,
            }
          : null

    return (
        <div className={styles.notecardEditor}>
            <div>
                <Button
                    onClick={saveNotecardChanges}
                    type={'primary'}
                    loading={isSaving}
                    disabled={!isUnsaved || isReadOnly}
                >
                    Save notecard changes
                </Button>
            </div>
            <div>
                <CopyIndividualContentButton
                    contentPath={notecard.contentPath}
                    type={'notecard'}
                    id={notecard.id}
                />
            </div>
            <div>
                <MoveIndividualContentButton
                    contentPath={notecard.contentPath}
                    type={'notecard'}
                    id={notecard.id}
                />
            </div>
            <div>
                <CopyDeepLinkButton
                    contentType={UniversalContentType.NOTECARD}
                    id={notecard.id}
                />
            </div>
            <Tabs
                activeKey={activeNotecardEditorContentType}
                onChange={(newTab) =>
                    setActiveNotecardEditorContentType(
                        newTab as NotecardEditorContentType
                    )
                }
                items={[
                    {
                        key: NotecardEditorContentType.metadata,
                        label: NotecardEditorContentType.metadata,
                        children: (
                            <NotecardMetadataEditor
                                contentCreatorEditableNotecardMetadata={
                                    contentCreatorEditableNotecardMetadata
                                }
                                setContentCreatorEditableNotecardMetadata={
                                    setContentCreatorEditableNotecardMetadataCallback
                                }
                                contentPath={notecard.contentPath}
                                notecardID={props.notecardID}
                            />
                        ),
                    },
                    {
                        key: NotecardEditorContentType.front,
                        label: NotecardEditorContentType.front,
                        children: (
                            <MagicTextBoxEditor
                                id={`${props.notecardID}-front`}
                                key={slimLeafNodeInvalidator}
                                namespace={'notecard front'}
                                placeholder={'Enter notecard front.'}
                                initialStringifiedEditorState={notecard.front}
                                editorUpdateListener={
                                    debouncedNotecardFrontUpdateListener
                                }
                                savableProps={{
                                    onSave: saveNotecardChanges,
                                    setHasUnsavedChanges: () => undefined,
                                }}
                            />
                        ),
                    },
                    {
                        key: NotecardEditorContentType.back,
                        label: NotecardEditorContentType.back,
                        children: (
                            <MagicTextBoxEditor
                                id={`${props.notecardID}-back`}
                                key={slimLeafNodeInvalidator}
                                namespace={'notecard back'}
                                placeholder={'Enter notecard back.'}
                                initialStringifiedEditorState={notecard.back}
                                editorUpdateListener={
                                    debouncedNotecardBackUpdateListener
                                }
                                savableProps={{
                                    onSave: saveNotecardChanges,
                                    setHasUnsavedChanges: () => undefined,
                                }}
                            />
                        ),
                    },
                    {
                        key: NotecardEditorContentType.commentThread,
                        label: NotecardEditorContentType.commentThread,
                        disabled: hasNeverBeenSaved || authState.isApplication,
                        children: (
                            <ContentCommentThreadWrapper
                                contentType={ContentType.NOTECARD}
                                contentId={props.notecardID}
                                contentPath={notecard.contentPath}
                            />
                        ),
                    },
                    {
                        key: NotecardEditorContentType.convertToPracticeProblem,
                        label: NotecardEditorContentType.convertToPracticeProblem,
                        children: (
                            <>
                                {!displayedConversion &&
                                    !conversionErrorJobId &&
                                    (() => {
                                        const latest = cardJobHistory?.[0]
                                        if (latest?.endedAt) {
                                            return (
                                                <Alert
                                                    type={
                                                        latest.status ===
                                                        AgentContentJobStatus.succeeded
                                                            ? 'success'
                                                            : 'warning'
                                                    }
                                                    showIcon
                                                    style={{
                                                        marginBottom: '1rem',
                                                    }}
                                                    message={
                                                        <Typography.Text>
                                                            {`Last agent run: ${latest.status} · ${getAgentModelLabel(latest.modelName)} · by ${latest.requestedByDisplayName ?? 'unknown'} · ${formatRelativeTime(latest.endedAt)} `}
                                                            <Button
                                                                type={'link'}
                                                                size={'small'}
                                                                onClick={() =>
                                                                    openRunDetails(
                                                                        latest.jobId
                                                                    )
                                                                }
                                                            >
                                                                View run
                                                            </Button>
                                                        </Typography.Text>
                                                    }
                                                    description={
                                                        latest.instructorRequest ? (
                                                            <Typography.Text
                                                                italic
                                                            >
                                                                {`"${latest.instructorRequest}"`}
                                                            </Typography.Text>
                                                        ) : undefined
                                                    }
                                                />
                                            )
                                        }
                                        if (
                                            !doesNotecardAsPracticeProblemExist
                                        ) {
                                            return (
                                                <Alert
                                                    type={'info'}
                                                    showIcon
                                                    style={{
                                                        marginBottom: '1rem',
                                                    }}
                                                    message={
                                                        'No practice problem counterpart yet — generate one with the agent.'
                                                    }
                                                />
                                            )
                                        }
                                        return null
                                    })()}
                                <div style={{ paddingBottom: '1rem' }}>
                                    <Input.TextArea
                                        placeholder={
                                            doesNotecardAsPracticeProblemExist
                                                ? 'Prompt for the agent — required for Modify (the change to make), optional guidance for Regenerate...'
                                                : 'Optional guidance for the agent (e.g. "emphasize the regulatory angle", "frame as compare-and-contrast")...'
                                        }
                                        value={steeringText}
                                        onChange={(event) =>
                                            setSteeringText(event.target.value)
                                        }
                                        autoSize={{ minRows: 1, maxRows: 4 }}
                                        style={{ marginBottom: '0.5rem' }}
                                        disabled={!!displayedConversion}
                                    />
                                    <Tooltip
                                        title={
                                            convertNotecardToPracticeProblemButtonDisabledText
                                        }
                                        placement={'right'}
                                    >
                                        {doesNotecardAsPracticeProblemExist ? (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '0.5rem',
                                                }}
                                            >
                                                <Tooltip
                                                    title={
                                                        steeringText.trim()
                                                            ? ''
                                                            : 'Describe the change you want in the box above first'
                                                    }
                                                >
                                                    <Popconfirm
                                                        title={
                                                            'Modify this practice problem?'
                                                        }
                                                        description={
                                                            'The agent will apply your requested change to the existing practice problem and keep the rest as is.'
                                                        }
                                                        okText={'Modify'}
                                                        onConfirm={() =>
                                                            void convertNotecardToPracticeProblemCallback(
                                                                true
                                                            )
                                                        }
                                                        disabled={
                                                            !!convertNotecardToPracticeProblemButtonDisabledText ||
                                                            !!displayedConversion ||
                                                            !steeringText.trim()
                                                        }
                                                    >
                                                        <Button
                                                            type={'primary'}
                                                            disabled={
                                                                !!convertNotecardToPracticeProblemButtonDisabledText ||
                                                                !!displayedConversion ||
                                                                !steeringText.trim()
                                                            }
                                                            loading={
                                                                isConvertingNotecardToPracticeProblem ||
                                                                displayedConversion?.status ===
                                                                    AgentContentJobStatus.running
                                                            }
                                                        >
                                                            <AiSparkleIcon />{' '}
                                                            Modify
                                                        </Button>
                                                    </Popconfirm>
                                                </Tooltip>
                                                <Popconfirm
                                                    title={
                                                        'Regenerate this practice problem?'
                                                    }
                                                    description={
                                                        'The agent will author a fresh counterpart and REPLACE the existing practice problem.'
                                                    }
                                                    okText={'Regenerate'}
                                                    onConfirm={() =>
                                                        void convertNotecardToPracticeProblemCallback(
                                                            false
                                                        )
                                                    }
                                                    disabled={
                                                        !!convertNotecardToPracticeProblemButtonDisabledText ||
                                                        !!displayedConversion
                                                    }
                                                >
                                                    <Button
                                                        disabled={
                                                            !!convertNotecardToPracticeProblemButtonDisabledText ||
                                                            !!displayedConversion
                                                        }
                                                        loading={
                                                            isConvertingNotecardToPracticeProblem ||
                                                            displayedConversion?.status ===
                                                                AgentContentJobStatus.running
                                                        }
                                                    >
                                                        <AiSparkleIcon />{' '}
                                                        Regenerate
                                                    </Button>
                                                </Popconfirm>
                                            </div>
                                        ) : (
                                            <Button
                                                type={'primary'}
                                                disabled={
                                                    !!convertNotecardToPracticeProblemButtonDisabledText ||
                                                    !!displayedConversion
                                                }
                                                onClick={() =>
                                                    void convertNotecardToPracticeProblemCallback(
                                                        false
                                                    )
                                                }
                                                loading={
                                                    isConvertingNotecardToPracticeProblem ||
                                                    displayedConversion?.status ===
                                                        AgentContentJobStatus.running
                                                }
                                            >
                                                <AiSparkleIcon /> Generate
                                            </Button>
                                        )}
                                    </Tooltip>
                                </div>
                                {displayedConversion && (
                                    <Alert
                                        type={'info'}
                                        showIcon
                                        style={{ marginBottom: '1rem' }}
                                        message={`${AGENT_MODE_LABEL[AgentContentJobMode.counterpartBackfill]} · ${getAgentModelLabel(COUNTERPART_MODEL)}`}
                                        description={
                                            <Typography.Text>
                                                {displayedConversion.status ===
                                                AgentContentJobStatus.waitingForLock
                                                    ? 'waiting for another run to finish'
                                                    : displayedConversion.status}{' '}
                                                ·{' '}
                                                {activeConversionJob?.startedAt
                                                    ? 'started'
                                                    : 'created'}{' '}
                                                {new Intl.RelativeTimeFormat(
                                                    undefined,
                                                    { numeric: 'auto' }
                                                ).format(
                                                    -Math.max(
                                                        0,
                                                        Math.floor(
                                                            (Date.now() -
                                                                new Date(
                                                                    displayedConversion.createdAt
                                                                ).getTime()) /
                                                                60_000
                                                        )
                                                    ),
                                                    'minute'
                                                )}{' '}
                                                {displayedConversion.jobId && (
                                                    <Button
                                                        type={'link'}
                                                        size={'small'}
                                                        onClick={() =>
                                                            openRunDetails(
                                                                displayedConversion.jobId as string
                                                            )
                                                        }
                                                    >
                                                        View run
                                                    </Button>
                                                )}
                                            </Typography.Text>
                                        }
                                    />
                                )}
                                {conversionErrorJobId && (
                                    <Alert
                                        type={'error'}
                                        showIcon
                                        style={{ marginBottom: '1rem' }}
                                        message={
                                            'Practice problem generation did not complete.'
                                        }
                                        description={
                                            <Button
                                                type={'link'}
                                                size={'small'}
                                                onClick={() =>
                                                    openRunDetails(
                                                        conversionErrorJobId
                                                    )
                                                }
                                            >
                                                View run
                                            </Button>
                                        }
                                    />
                                )}
                                {doesNotecardAsPracticeProblemExist && (
                                    <PracticeProblemEditor
                                        practiceProblemID={props.notecardID}
                                    />
                                )}
                            </>
                        ),
                    },
                ]}
            />
        </div>
    )
}
